<template>
  <v-dialog v-model="dialogVisible" max-width="900" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>{{ $t('spedilink.companyBlacklist.title') }}</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="!canEdit" type="info" outlined dense class="mb-4">
          {{ $t('spedilink.companyBlacklist.readOnlyInfo') }}
        </v-alert>

        <v-tabs v-model="activeTab" grow>
          <v-tab>{{ $t('spedilink.companyBlacklist.companiesTab') }}</v-tab>
          <v-tab>{{ $t('spedilink.companyBlacklist.personsTab') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="mt-4">
          <v-tab-item>
            <div class="subtitle-2 mb-2">{{ $t('spedilink.companyBlacklist.currentlyBlocked') }}</div>
            <v-data-table
              :items="blockedCompanies"
              :headers="companyBlockedHeaders"
              :loading="loadingBlacklist"
              hide-default-footer
              disable-pagination
              dense
              class="mb-6"
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn v-if="canEdit" icon small :loading="isTogglingCompany(item.id)" @click="toggleCompany(item, false)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <span class="grey--text">{{ $t('spedilink.companyBlacklist.noBlockedCompanies') }}</span>
              </template>
            </v-data-table>

            <template v-if="canEdit">
              <div class="subtitle-2 mb-2">{{ $t('spedilink.companyBlacklist.addSection') }}</div>
              <v-text-field
                v-model="companySearch"
                outlined
                dense
                clearable
                hide-details="auto"
                :placeholder="$t('spedilink.companyBlacklist.companySearchPlaceholder')"
              />
              <div v-if="companySearch && companySearch.length < 3" class="caption grey--text mt-2">
                {{ $t('spedilink.companyBlacklist.searchMinChars') }}
              </div>
              <v-progress-linear v-if="companySearchLoading" indeterminate color="primary" class="my-2" />
              <v-list v-else-if="companySearchResults.length > 0" dense class="overflow-y-auto mt-2" style="max-height: 280px;">
                <v-list-item
                  v-for="item in companySearchResults"
                  :key="item.id"
                  ripple
                  @click="toggleCompany(item, !item.isBlocked)"
                >
                  <v-list-item-action class="my-0 mr-2">
                    <v-checkbox
                      :input-value="item.isBlocked"
                      color="primary"
                      readonly
                      :loading="isTogglingCompany(item.id)"
                      @click.stop="toggleCompany(item, !item.isBlocked)"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('spedilink.companyBlacklist.vatNumber') }}: {{ item.vatNumber }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-tab-item>

          <v-tab-item>
            <div class="subtitle-2 mb-2">{{ $t('spedilink.companyBlacklist.currentlyBlocked') }}</div>
            <v-data-table
              :items="blockedPersons"
              :headers="personBlockedHeaders"
              :loading="loadingBlacklist"
              hide-default-footer
              disable-pagination
              dense
              class="mb-6"
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn v-if="canEdit" icon small :loading="isTogglingPerson(item.id)" @click="togglePerson(item, false)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <span class="grey--text">{{ $t('spedilink.companyBlacklist.noBlockedPersons') }}</span>
              </template>
            </v-data-table>

            <template v-if="canEdit">
              <div class="subtitle-2 mb-2">{{ $t('spedilink.companyBlacklist.addSection') }}</div>
              <v-text-field
                v-model="personSearch"
                outlined
                dense
                clearable
                hide-details="auto"
                :placeholder="$t('spedilink.companyBlacklist.personSearchPlaceholder')"
              />
              <div v-if="personSearch && personSearch.length < 3" class="caption grey--text mt-2">
                {{ $t('spedilink.companyBlacklist.searchMinChars') }}
              </div>
              <v-progress-linear v-if="personSearchLoading" indeterminate color="primary" class="my-2" />
              <v-list v-else-if="personSearchResults.length > 0" dense class="overflow-y-auto mt-2" style="max-height: 280px;">
                <v-list-item
                  v-for="item in personSearchResults"
                  :key="item.id"
                  ripple
                  @click="togglePerson(item, !item.isBlocked)"
                >
                  <v-list-item-action class="my-0 mr-2">
                    <v-checkbox
                      :input-value="item.isBlocked"
                      color="primary"
                      readonly
                      :loading="isTogglingPerson(item.id)"
                      @click.stop="togglePerson(item, !item.isBlocked)"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.firstName }} {{ item.lastName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}<span v-if="item.companyName"> · {{ item.companyName }}</span></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions class="justify-end px-4 pb-4">
        <v-btn color="primary" depressed @click="close">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Service from '@/services'

const SEARCH_DEBOUNCE_MS = 400
const SEARCH_PAGE_SIZE = 50

export default {
  name: 'SpedilinkCompanyBlacklistDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      activeTab: 0,
      loadingBlacklist: false,
      canEdit: false,
      blockedCompanies: [],
      blockedPersons: [],
      companySearch: '',
      personSearch: '',
      companySearchResults: [],
      personSearchResults: [],
      companySearchLoading: false,
      personSearchLoading: false,
      companySearchTimeout: null,
      personSearchTimeout: null,
      togglingCompanyIds: [],
      togglingPersonIds: [],
      companyBlockedHeaders: [
        { text: this.$t('spedilink.companyBlacklist.companyName'), value: 'name' },
        { text: this.$t('spedilink.companyBlacklist.vatNumber'), value: 'vatNumber' },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ],
      personBlockedHeaders: [
        { text: this.$t('spedilink.persons.firstName'), value: 'firstName' },
        { text: this.$t('spedilink.persons.lastName'), value: 'lastName' },
        { text: this.$t('spedilink.persons.email'), value: 'email' },
        { text: this.$t('spedilink.offers.company'), value: 'companyName' },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.loadBlacklist()
      } else {
        this.resetState()
      }
    },
    companySearch (val) {
      clearTimeout(this.companySearchTimeout)
      this.companySearchTimeout = setTimeout(() => {
        this.searchCompanies(val)
      }, SEARCH_DEBOUNCE_MS)
    },
    personSearch (val) {
      clearTimeout(this.personSearchTimeout)
      this.personSearchTimeout = setTimeout(() => {
        this.searchPersons(val)
      }, SEARCH_DEBOUNCE_MS)
    }
  },
  methods: {
    resetState () {
      this.activeTab = 0
      this.companySearch = ''
      this.personSearch = ''
      this.companySearchResults = []
      this.personSearchResults = []
    },
    close () {
      this.dialogVisible = false
    },
    isTogglingCompany (id) {
      return this.togglingCompanyIds.includes(id)
    },
    isTogglingPerson (id) {
      return this.togglingPersonIds.includes(id)
    },
    async loadBlacklist () {
      this.loadingBlacklist = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkCompanyBlacklist/GetCompanyBlacklist')
        this.canEdit = data.canEdit || data.CanEdit || false
        this.blockedCompanies = data.companies || data.Companies || []
        this.blockedPersons = data.persons || data.Persons || []
      } catch (e) {
        this.canEdit = false
        this.blockedCompanies = []
        this.blockedPersons = []
      } finally {
        this.loadingBlacklist = false
      }
    },
    async searchCompanies (search) {
      if (!search || search.length < 3) {
        this.companySearchResults = []
        return
      }
      this.companySearchLoading = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkCompanyBlacklist/SearchCompanies', {
          search,
          page: 1,
          pageSize: SEARCH_PAGE_SIZE
        })
        this.companySearchResults = data.results || data.Results || []
      } catch (e) {
        this.companySearchResults = []
      } finally {
        this.companySearchLoading = false
      }
    },
    async searchPersons (search) {
      if (!search || search.length < 3) {
        this.personSearchResults = []
        return
      }
      this.personSearchLoading = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkCompanyBlacklist/SearchPersons', {
          search,
          page: 1,
          pageSize: SEARCH_PAGE_SIZE
        })
        this.personSearchResults = data.results || data.Results || []
      } catch (e) {
        this.personSearchResults = []
      } finally {
        this.personSearchLoading = false
      }
    },
    async toggleCompany (item, shouldBlock) {
      if (!this.canEdit || this.isTogglingCompany(item.id)) {
        return
      }
      this.togglingCompanyIds.push(item.id)
      try {
        const endpoint = shouldBlock
          ? 'spedilink/SpediLinkCompanyBlacklist/AddCompany'
          : 'spedilink/SpediLinkCompanyBlacklist/RemoveCompany'
        await Service.post(endpoint, { companyId: item.id })
        await this.loadBlacklist()
        if (this.companySearch && this.companySearch.length >= 3) {
          await this.searchCompanies(this.companySearch)
        }
        const messageKey = shouldBlock
          ? 'spedilink.companyBlacklist.companyAdded'
          : 'spedilink.companyBlacklist.companyRemoved'
        await this.$store.dispatch('setSuccessNotification', this.$t(messageKey))
      } catch (e) {
      } finally {
        this.togglingCompanyIds = this.togglingCompanyIds.filter(id => id !== item.id)
      }
    },
    async togglePerson (item, shouldBlock) {
      if (!this.canEdit || this.isTogglingPerson(item.id)) {
        return
      }
      this.togglingPersonIds.push(item.id)
      try {
        const endpoint = shouldBlock
          ? 'spedilink/SpediLinkCompanyBlacklist/AddPerson'
          : 'spedilink/SpediLinkCompanyBlacklist/RemovePerson'
        await Service.post(endpoint, { personId: item.id })
        await this.loadBlacklist()
        if (this.personSearch && this.personSearch.length >= 3) {
          await this.searchPersons(this.personSearch)
        }
        const messageKey = shouldBlock
          ? 'spedilink.companyBlacklist.personAdded'
          : 'spedilink.companyBlacklist.personRemoved'
        await this.$store.dispatch('setSuccessNotification', this.$t(messageKey))
      } catch (e) {
      } finally {
        this.togglingPersonIds = this.togglingPersonIds.filter(id => id !== item.id)
      }
    }
  }
}
</script>
